import { useEffect, useRef } from 'react';

/**
 * useHistoryBack — wires a transient piece of UI (a modal, a drawer, a multi-select
 * mode, etc.) into the browser history stack so that:
 *
 *   • the OS / browser back gesture (Android hardware back, iOS swipe-from-edge,
 *     desktop Alt-Left) dismisses the UI instead of leaving the app entirely,
 *   • the React state stays in sync with `history` so nothing leaks if the user
 *     navigates away while the UI is open.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   useHistoryBack(open, () => setOpen(false), 'add-task-modal');
 *
 * Internals:
 *   • When `open` flips from false → true we push a `{ historyKey }` state entry.
 *   • A `popstate` listener on that entry calls `onBack` and tears itself down.
 *   • If the UI is closed by other means (X button, save action), the hook also
 *     unwinds the matching entry so the back stack does not grow indefinitely.
 *
 * The `historyKey` only has to be unique among UIs that may be open at the same
 * time — collisions across unrelated screens are fine.
 */
export default function useHistoryBack(open, onBack, historyKey) {
  // We need a stable ref to the latest onBack callback so the popstate listener
  // never closes over a stale function.
  const onBackRef = useRef(onBack);
  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  // Did THIS hook instance push the entry? Tracks ownership so onClose'd-by-other-means
  // doesn't accidentally pop someone else's history entry.
  const pushedRef = useRef(false);

  useEffect(() => {
    if (!open) return undefined;

    // Skip on SSR / non-browser environments.
    if (typeof window === 'undefined' || !window.history) return undefined;

    // Push a new history entry tagged with our key so the back gesture has
    // something to pop.
    try {
      window.history.pushState({ __historyBack: historyKey }, '');
      pushedRef.current = true;
    } catch {
      // Some embedded browsers throw on pushState — degrade gracefully.
      return undefined;
    }

    const handlePopState = (event) => {
      // If the user navigated back PAST our entry, the state will no longer have
      // our key — that's our signal to dismiss the UI.
      const matchesOurs =
        !event.state ||
        event.state.__historyBack !== historyKey;
      if (matchesOurs) {
        pushedRef.current = false;
        // Tell the global back-button handler this back was consumed here
        window.__historyBackConsumedAt = Date.now();
        try {
          onBackRef.current?.();
        } catch (err) {
          console.error('useHistoryBack onBack threw:', err);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // If the UI is being closed by user action (not the back gesture), unwind
      // the history entry we pushed so the back stack stays clean.
      if (pushedRef.current) {
        pushedRef.current = false;
        const current = window.history.state;
        if (current && current.__historyBack === historyKey) {
          try {
            // Tell the global back-button handler this pop is ours, not a user "back"
            window.__historyBackUnwinding = true;
            window.history.back();
          } catch {
            /* noop */
          }
        } else if (!current || current.__historyBack !== historyKey) {
          // Our entry was just popped by the back gesture but we unmounted before our
          // listener ran (parent re-render) — still claim this back.
          window.__historyBackConsumedAt = Date.now();
        }
      }
    };
  }, [open, historyKey]);
}
